import { useMemo } from 'react';
import { quadtree } from 'd3-quadtree';
import { SeriesPointXY } from '../Types';
import { definedAccessorXY, pointXAccessor, pointYAccessor } from './';

interface Opts {
  axis?: 'x' | 'xy' | 'y';
  radius?: number;
}

/**
 * A react hook that given a series of points, will return a function
 * that takes a point and returns the nearest point within the series.
 *
 * @param series The SeriesPointXY point that corresponds to a
 * an x and y coordinate on the data domain, which the returned
 * ReferenceElement will be created for.
 *
 * @returns A function that takes a point and returns a point or null
 */
const useGetNearestPoint = (series?: SeriesPointXY[], opts: Opts = {}) => {
  const { axis, radius } = opts;

  return useMemo(() => {
    const tree = quadtree<SeriesPointXY>()
      .x(axis === 'y' ? () => 0 : pointXAccessor)
      .y(axis === 'x' ? () => 0 : pointYAccessor)
      .addAll(series ? series.filter(definedAccessorXY) : []);

    return (point: null | SeriesPointXY) => {
      if (point === null || point.x === null || point.y === null) {
        return null;
      }

      return tree.find(point.x, point.y, radius) || null;
    };
  }, [series, axis, radius]);
};

export default useGetNearestPoint;
